/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {makeStyles} from '@material-ui/core/styles';
import {toggleThemeStyle} from '../theme/helpers';
import {
  dlsAlt,
  dlsColor,
  dlsOverlay,
  dlsShadow,
  dlsZIndex
} from '../theme/theme';
import {DLSTheme} from '../types/types';

export const APPLIED_FILTER_BAR_CHIP_GAP            = 8;
export const APPLIED_FILTER_BAR_CONTENT_GAP         = 8;
export const APPLIED_FILTER_BAR_MOBILE_SIDE_PADDING = 12;

export const useAppliedFilterBarStyles = makeStyles((theme: DLSTheme) => ({
  root : {
    alignItems      : 'center',
    backgroundColor : dlsAlt('components', 'appliedFilterBar', 'backgroundColor'),
    backdropFilter  : 'blur(2px)',
    boxSizing       : 'border-box',
    display         : 'flex',
    flexDirection   : 'row',
    flexShrink      : 0,
    gap             : 8,
    height          : 48,
    left            : 0,
    marginBottom    : -48,
    padding         : '0 24px',
    position        : 'sticky',
    right           : 0,
    top             : 0,
    width           : '100%',
    zIndex          : ({isModularSearch}: {isModularSearch?: boolean}) => (
      isModularSearch ? dlsZIndex('modularSearchDisplayQueryChips') : dlsZIndex('trackingFilterSelectedFiltersBar')
    ),
    [theme.breakpoints.down('sm')] : {
      padding : `0 ${APPLIED_FILTER_BAR_MOBILE_SIDE_PADDING}px`
    }
  },
  content : {
    alignItems : 'center',
    display    : 'flex',
    gap        : APPLIED_FILTER_BAR_CONTENT_GAP,
    minWidth   : 0,
    // the row never widens the page - chips past its edge (e.g. before the
    // overflow count settles) gave the page scrollbars, which resized the
    // map and grid, which re-counted the chips, back and forth. Clipped on
    // this axis only, so the chips' shadows still show ...
    overflowX  : 'clip' as any,
    width      : '100%'
  },
  labelGroup : {
    alignItems   : 'center',
    borderRadius : 8,
    display      : 'flex',
    flexShrink   : 0,
    gap          : 8,
    height       : 18
  },
  label : {
    color      : dlsColor('grey1'),
    fontFamily : 'Inter',
    fontSize   : 12,
    fontWeight : 600,
    lineHeight : '16px',
    textShadow : dlsAlt('components', 'appliedFilterBar', 'labelTextShadow'),
    whiteSpace : 'nowrap'
  },
  count : {
    alignItems      : 'center',
    backgroundColor : dlsColor('darkestBlue'),
    border          : `1px solid ${dlsColor('white')}`,
    borderRadius    : 25,
    boxShadow       : dlsShadow(3),
    boxSizing       : 'border-box',
    color           : dlsColor('white'),
    display         : 'flex',
    fontFamily      : 'Inter',
    fontSize        : 12,
    fontWeight      : 500,
    height          : 18,
    justifyContent  : 'center',
    lineHeight      : '16px',
    minWidth        : 18,
    // the 18px circle less its 1px border leaves exactly the 16px line -
    // any vertical padding squeezes the line and pushes the number low ...
    padding         : '0 4px'
  },
  favoriteIcon : {
    alignItems     : 'center',
    display        : 'flex',
    flexShrink     : 0,
    height         : 24,
    justifyContent : 'center',
    width          : 24
  },
  chipArea : {
    alignItems : 'center',
    boxSizing  : 'border-box',
    display    : 'flex',
    // does NOT grow to fill the row - the "more"/Clear controls that follow
    // it (in the same flex row) must sit immediately after the last visible
    // chip, not stretched to the far end of the bar. useAppliedFilterBarMeasurements
    // computes visibleCount from container.clientWidth and each ref's own
    // getBoundingClientRect(), not from this element's CSS, so this is a
    // purely visual change...
    flexGrow   : 0,
    gap        : APPLIED_FILTER_BAR_CHIP_GAP,
    minWidth   : 0,
    overflow   : 'hidden',
    padding    : '0 8px'
  },
  chip : {
    '&&' : {
      alignItems      : 'center',
      backgroundColor : dlsColor('paleBlue'),
      borderRadius    : 4,
      boxShadow       : dlsShadow(3),
      boxSizing       : 'border-box',
      display         : 'flex',
      flexDirection   : 'row',
      flexGrow        : 0,
      flexShrink      : 0,
      gap             : 4,
      height          : 24,
      margin          : 0,
      maxWidth        : 220,
      minHeight       : 24,
      // DLS Tag padding (current theme) - legacy keeps its 8px sides ...
      padding         : toggleThemeStyle('4px 8px', '4px')
    },
    '& .MuiChip-label' : {
      alignItems    : 'center',
      color         : dlsColor('grey2'),
      display       : 'flex',
      flexGrow      : 0,
      flexShrink    : 1,
      fontFamily    : 'Inter',
      fontSize      : 12,
      fontWeight    : 600,
      height        : 16,
      letterSpacing : 0.5,
      lineHeight    : '16px',
      maxWidth      : 160,
      overflow      : 'hidden',
      paddingLeft   : 0,
      paddingRight  : 0,
      textOverflow  : 'ellipsis'
    },
    '& .MuiChip-deleteIcon' : {
      alignItems      : 'center',
      backgroundColor : 'rgba(31, 144, 174, 0.15)',
      borderRadius    : 4,
      display         : 'flex',
      height          : 16,
      justifyContent  : 'center',
      margin          : '0 !important',
      width           : 16
    },
    '& .MuiChip-deleteIcon > div' : {
      marginRight : '0 !important'
    }
  },
  closeIcon : {
    alignItems      : 'center',
    backgroundColor : 'rgba(31, 144, 174, 0.15)',
    borderRadius    : 4,
    display         : 'flex',
    height          : 16,
    justifyContent  : 'center',
    width           : 16
  },
  moreAction : {
    flexShrink : 0
  },
  moreSizer : {
    left          : -10000,
    pointerEvents : 'none',
    position      : 'fixed',
    top           : -10000,
    visibility    : 'hidden'
  },
  // PrimaryButton only ever forwards the LAST `classes` prop it receives to
  // the underlying MUI Button (its own JSX spreads `{...props}` after its
  // own `classes` attribute), so passing a custom `classes.root` here
  // entirely replaces - rather than merges with - its own base `.root`
  // styling; primaryRoot must therefore be a complete, self-contained spec
  // (color/font/radius included), not a partial override layered on top of
  // assumed inherited defaults. TertiaryButton (current/TertiaryButton.tsx)
  // correctly merges classes.root via clsx, so tertiaryRoot below stays a
  // complete spec too, mostly for consistency with primaryRoot rather than
  // strict necessity...
  tertiaryRoot : {
    '&&' : {
      backgroundColor : 'transparent',
      borderRadius    : '8px !important',
      boxShadow       : 'none',
      color           : dlsColor('darkestBlue'),
      fontFamily      : 'Inter',
      fontSize        : 13,
      fontWeight      : 600,
      height          : 32,
      letterSpacing   : 0.5,
      lineHeight      : '14px',
      minWidth        : 0,
      padding         : '8px 16px',
      textTransform   : 'none',
      whiteSpace      : 'nowrap',
      width           : 'auto'
    }
  },
  barActionRoot : {
    '&&' : {
      alignItems      : 'center',
      backgroundColor : 'transparent',
      borderRadius    : '8px !important',
      boxShadow       : 'none',
      boxSizing       : 'border-box',
      color           : dlsColor('darkestBlue'),
      display         : 'flex',
      flexDirection   : 'row',
      flexGrow        : 0,
      flexShrink      : 0,
      fontFamily      : 'Inter',
      fontSize        : 13,
      fontWeight      : 600,
      gap             : 4,
      height          : 24,
      justifyContent  : 'center',
      letterSpacing   : 0.5,
      lineHeight      : '16px',
      minHeight       : 24,
      minWidth        : 0,
      padding         : '4px 16px',
      whiteSpace      : 'nowrap'
    },
    '& .MuiButton-label' : {
      alignItems     : 'center',
      display        : 'flex',
      height         : 16,
      justifyContent : 'center',
      lineHeight     : '16px'
    },
    '& .MuiGrid-container' : {
      alignItems     : 'center',
      display        : 'flex',
      flexDirection  : 'row',
      flexWrap       : 'nowrap',
      gap            : 4,
      height         : 16,
      justifyContent : 'center',
      width          : 'auto'
    },
    '& .MuiGrid-container > span' : {
      height      : 16,
      lineHeight  : '16px',
      marginLeft  : '0 !important',
      marginRight : '0 !important',
      marginTop   : '0 !important'
    }
  },
  clearGroup : {
    alignItems : 'center',
    display    : 'flex',
    flexShrink : 0,
    gap        : 8
  },
  divider : {
    backgroundColor : dlsColor('grey2'),
    height          : 32,
    opacity         : 0.6,
    width           : 1
  },
  measuring : {
    alignItems    : 'center',
    display       : 'flex',
    gap           : APPLIED_FILTER_BAR_CHIP_GAP,
    left          : -10000,
    pointerEvents : 'none',
    position      : 'fixed',
    top           : -10000,
    visibility    : 'hidden'
  },
  scrim : {
    backgroundColor : dlsOverlay('black'),
    bottom          : 0,
    height          : '100vh',
    left            : 0,
    position        : 'fixed',
    right           : 0,
    top             : 0,
    zIndex          : dlsZIndex('trackingFilterSelectedFiltersScrim')
  },
  overflowModal : {
    backgroundColor                : dlsColor('white'),
    borderRadius                   : 12,
    borderTop                      : `2px solid ${dlsColor('pastelTeal')}`,
    boxShadow                      : dlsShadow(2.1),
    boxSizing                      : 'border-box',
    display                        : 'flex',
    flexDirection                  : 'column',
    maxHeight                      : 'calc(100vh - 88px)',
    overflow                       : 'hidden',
    padding                        : '0 0 24px',
    position                       : 'fixed',
    right                          : 24,
    top                            : 64,
    width                          : 432,
    zIndex                         : dlsZIndex('trackingFilterSelectedFiltersModal'),
    [theme.breakpoints.down('sm')] : {
      left    : APPLIED_FILTER_BAR_MOBILE_SIDE_PADDING,
      right   : APPLIED_FILTER_BAR_MOBILE_SIDE_PADDING,
      width   : 'auto',
      // the 0 top padding above is a desktop-only spec change - mobile
      // keeps its original 16px top inset ...
      padding : '16px 0 24px'
    }
  },
  modalHeader : {
    alignItems     : 'center',
    display        : 'flex',
    flexShrink     : 0,
    height         : 56,
    justifyContent : 'space-between',
    padding        : '0 16px'
  },
  modalTitle : {
    color      : dlsColor('grey1'),
    fontFamily : 'Inter',
    fontSize   : 16,
    fontWeight : 600,
    lineHeight : '20px'
  },
  modalChips : {
    overflowY : 'auto',
    padding   : 0
  },
  modalChipGroup : {
    alignContent   : 'center',
    alignItems     : 'center',
    backdropFilter : 'blur(2px)',
    borderRadius   : 4,
    boxSizing      : 'border-box',
    display        : 'flex',
    flexDirection  : 'row',
    flexWrap       : 'wrap',
    gap            : 16,
    minHeight      : 24,
    padding        : '0 16px',
    width          : '100%'
  },
  modalChipGroupSpacing : {
    marginTop : 32
  },
  modalGroupLabel : {
    alignItems    : 'center',
    color         : dlsColor('grey1'),
    display       : 'flex',
    flexShrink    : 0,
    // the current theme's alias for its Archivo Narrow font files ...
    fontFamily    : 'Roboto',
    fontSize      : 12,
    fontWeight    : 700,
    height        : 24,
    letterSpacing : 1,
    lineHeight    : '14px',
    marginRight   : 4,
    textTransform : 'uppercase',
    whiteSpace    : 'nowrap'
  },
  modalChipGroupChip : {
    '&&' : {
      marginBottom : 0,
      marginLeft   : 0,
      marginRight  : 0,
      marginTop    : 0
    }
  },
  confirmation : {
    backgroundColor                : dlsColor('white'),
    borderRadius                   : 12,
    borderTop                      : `2px solid ${dlsColor('pastelTeal')}`,
    boxShadow                      : dlsShadow(2.1),
    boxSizing                      : 'border-box',
    display                        : 'flex',
    flexDirection                  : 'column',
    // a longer message (the filter drawer's own confirmations) grows it ...
    minHeight                      : 196,
    position                       : 'fixed',
    right                          : 20,
    top                            : 64,
    width                          : 318,
    // over the scrim, in the layer it shares with it ...
    zIndex                         : 1,
    [theme.breakpoints.down('sm')] : {
      right : APPLIED_FILTER_BAR_MOBILE_SIDE_PADDING
    }
  },
  confirmationHeader : {
    alignItems     : 'center',
    display        : 'flex',
    height         : 56,
    justifyContent : 'space-between',
    padding        : '0 16px'
  },
  confirmationTitle : {
    color      : dlsColor('grey1'),
    fontFamily : 'Inter',
    fontSize   : 16,
    fontWeight : 600,
    lineHeight : '20px'
  },
  confirmationMessage : {
    color      : dlsColor('grey1'),
    flex       : 1,
    fontFamily : 'Inter',
    fontSize   : 14,
    fontWeight : 400,
    lineHeight : '20px',
    padding    : '8px 16px'
  },
  confirmationActions : {
    alignItems     : 'center',
    display        : 'flex',
    gap            : 8,
    height         : 64,
    justifyContent : 'flex-end',
    padding        : '8px 16px'
  },
  primaryRoot : {
    '&&' : {
      backgroundColor : dlsColor('darkTeal'),
      borderRadius    : '8px !important',
      color           : dlsColor('white'),
      fontFamily      : 'Inter',
      fontSize        : 14,
      fontWeight      : 500,
      height          : 32,
      lineHeight      : '16px',
      flexShrink      : 0,
      minWidth        : 112,
      padding         : '16px 8px',
      textTransform   : 'none',
      // its label on one line, as wide as it needs - Leave Tracking, Apply
      // Filter - never narrower than the bar's Clear All ...
      whiteSpace      : 'nowrap',
      width           : 'auto'
    },
    '& .MuiButton-label' : {
      gap : 8
    },
    '& .MuiButton-startIcon' : {
      marginLeft  : '0px !important',
      marginRight : '0px !important'
    }
  }
}) as any);
